/**
 * 태그드 턴 프로토콜 파서 — 결정 #26. AI 응답에서 자유 본문(narration)과 구조
 * 태그(choices / set / scene / roll …)를 분리한다.
 *
 * 설계 원칙(결정 #26): **관대함이 곧 견고함.** 태그가 깨지거나 누락돼도 본문
 * 내레이션은 항상 살아남아 게임이 멈추지 않는다(6s+ 소형 모델 호환). 알 수 없는
 * 태그는 무시하고, 인식한 구조 태그만 뽑은 뒤 나머지는 전부 narration으로 본다.
 *
 * 플랫폼 중립 순수 함수(결정 #10): DOM/네트워크 의존 없음. core에서 테스트한다.
 *
 * 인식하는 태그:
 *  - `<narration>...</narration>`  명시적 본문(언랩). 없으면 비태그 텍스트가 본문.
 *  - `<choices>` 안의 `<choice>...</choice>` 또는 줄/불릿 목록  선택지.
 *  - `<set key=hp value=5>` / inc / dec / push / unset  상태 연산(자기 종결).
 *  - `<scene bg=forest sprite=happy>`  장면/스프라이트 변경(자기 종결).
 *  - `<roll>2d6+1</roll>` 또는 `<roll dice=d20>`  주사위 요청.
 *  - RoQeet 게임 명령: `@선택지 { ... }`, `@설정 hp = 10`, `@증가 gold 5`,
 *    `@감소 hp 3`, `@추가 inventory "열쇠"`, `@삭제 poisoned`, `@장면 bg=forest`,
 *    `@주사위 d20`. 영문 alias도 함께 인식한다.
 */

/** 상태 변경 연산(결정 #26/#28). 모듈이 stateDelta를 세션 상태에 반영한다. */
export interface StateOp {
  /** set: 값 대입, inc/dec: 수치 증감, push: 배열에 추가(인벤토리), unset: 삭제. */
  op: "set" | "inc" | "dec" | "push" | "unset";
  key: string;
  /** unset 외에는 value 사용. inc/dec는 수치로 해석. */
  value?: string;
  /**
   * 대상 엔티티 이름(갭1). 지정되면 전역 키가 아니라 `state.entities[entity][key]`에
   * 적용된다(다중 NPC 스탯). 미지정이면 기존처럼 전역 평면 키. 태그 속성
   * `entity=` 또는 `npc=`로 지정한다. 예: `<inc key=aff value=10 entity=박아영>`.
   */
  entity?: string;
}

/** 장면/스프라이트 지정(비주얼노벨 등). 임의 키 허용. */
export type SceneSpec = Record<string, string>;

/** 주사위 요청(스탯 RPG 등). 실제 굴림은 rollDice로 분리(결정 #4 재현성). */
export interface DiceRequest {
  /** "d20", "2d6+3" 같은 표기. */
  notation: string;
  dc?: number;
  label?: string;
}

export interface TurnDiagnostic {
  severity: "warning";
  code: "invalid_action" | "unknown_action";
  /** 사람이 읽을 수 있는 거부/무시 사유. */
  message: string;
  /** 문제가 된 원본 action 조각. */
  source: string;
  line?: number;
}

/** 파싱된 한 턴. */
export interface ParsedTurn {
  /** 자유 본문(태그 제거 후, 명시적 narration 언랩 포함). */
  narration: string;
  /** 선택지(없으면 빈 배열). */
  choices: string[];
  /** 상태 변경 연산(순서 보존). */
  stateDelta: StateOp[];
  /** 장면 지정(있을 때만). */
  scene?: SceneSpec;
  /** 주사위 요청(없으면 빈 배열). */
  rolls: DiceRequest[];
  /** AI가 제안했지만 불완전/알 수 없어 엔진이 적용하지 않은 action 진단. */
  diagnostics: TurnDiagnostic[];
  /** 원본 텍스트(디버깅/감사용). */
  raw: string;
}

const STATE_OPS = new Set(["set", "inc", "dec", "push", "unset"]);
const ROQEET_STATE_OPS: Record<string, StateOp["op"]> = {
  설정: "set",
  set: "set",
  증가: "inc",
  inc: "inc",
  감소: "dec",
  dec: "dec",
  추가: "push",
  push: "push",
  삭제: "unset",
  unset: "unset",
};
const ROQEET_CHOICES = new Set(["선택지", "choices"]);
const ROQEET_SCENE = new Set(["장면", "scene"]);
const ROQEET_ROLL = new Set(["주사위", "roll"]);
const ROQEET_SCENE_ATTRS: Record<string, string> = {
  배경: "bg",
  배경이미지: "bg",
  스프라이트: "sprite",
  표정: "sprite",
  분위기: "mood",
};

/**
 * 태그 속성 문자열을 파싱한다. `key="v"`, `key='v'`, `key=v`(공백 없는 비인용),
 * 그리고 따옴표 안 공백을 지원. 베어 플래그는 빈 문자열 값으로.
 */
function parseAttrs(attrText: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  // key="..." | key='...' | key=unquoted | bareflag
  const re = /([\w-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))|([\w-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrText)) !== null) {
    if (m[1] !== undefined) {
      attrs[m[1]] = m[2] ?? m[3] ?? m[4] ?? "";
    } else if (m[5] !== undefined) {
      attrs[m[5]] = "";
    }
  }
  return attrs;
}

/** `<choices>` 블록 내부에서 선택지 목록을 뽑는다(여러 표기 지원). */
function parseChoices(inner: string): string[] {
  // 1) <choice>...</choice> 우선.
  const tagged = [...inner.matchAll(/<choice\b[^>]*>([\s\S]*?)<\/choice>/gi)].map((m) =>
    (m[1] ?? "").trim(),
  );
  if (tagged.length > 0) return tagged.filter(Boolean);

  // 2) 줄 단위(불릿/번호 접두 제거).
  return inner
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);
}

function readRoqeetBlock(
  lines: string[],
  start: number,
  rest: string,
): { inner: string; end: number } | undefined {
  const trimmedRest = rest.trim();
  if (!trimmedRest.startsWith("{")) return undefined;

  const first = trimmedRest.slice(1);
  const block: string[] = [];
  if (first.trim() === "}") return { inner: "", end: start };
  if (first.trim()) block.push(first);

  for (let i = start + 1; i < lines.length; i++) {
    const line = lines[i] ?? "";
    if (line.trim() === "}") return { inner: block.join("\n"), end: i };
    block.push(line);
  }
  return undefined;
}

function stripQuotes(value: string): string {
  const v = value.trim();
  if (
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"))
  ) {
    return v.slice(1, -1);
  }
  return v;
}

function parseDirectiveAttrs(attrText: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([\p{L}\w.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"']+))/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(attrText)) !== null) {
    attrs[m[1]!] = m[2] ?? m[3] ?? m[4] ?? "";
  }
  return attrs;
}

function splitDirectiveOptions(rest: string): { body: string; entity?: string } {
  let entity: string | undefined;
  const body = rest
    .replace(
      /\s+(?:entity|npc|대상)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"']+))/gu,
      (_full, dq: string | undefined, sq: string | undefined, bare: string | undefined) => {
        entity = dq ?? sq ?? bare ?? entity;
        return "";
      },
    )
    .trim();
  return entity ? { body, entity } : { body };
}

function parseRoqeetStateOp(op: StateOp["op"], rest: string): StateOp | undefined {
  const { body, entity } = splitDirectiveOptions(rest);
  let key = "";
  let value: string | undefined;

  if (op === "set") {
    const eq = /^([^\s=]+)\s*=\s*(.+)$/u.exec(body);
    if (eq) {
      key = eq[1]!.trim();
      value = stripQuotes(eq[2] ?? "");
    } else {
      const pair = /^([^\s=]+)\s+(.+)$/u.exec(body);
      if (pair) {
        key = pair[1]!.trim();
        value = stripQuotes(pair[2] ?? "");
      }
    }
  } else if (op === "unset") {
    const m = /^([^\s=]+)$/u.exec(body);
    key = (m?.[1] ?? "").trim();
  } else {
    const pair = /^([^\s=]+)\s+(.+)$/u.exec(body);
    if (pair) {
      key = pair[1]!.trim();
      value = stripQuotes(pair[2] ?? "");
    }
  }

  if (!key) return undefined;
  const parsed: StateOp = value !== undefined ? { op, key, value } : { op, key };
  if (entity) parsed.entity = entity;
  return parsed;
}

function parseRoqeetScene(rest: string): SceneSpec | undefined {
  const attrs = parseDirectiveAttrs(rest);
  const scene: SceneSpec = {};
  for (const [rawKey, value] of Object.entries(attrs)) {
    const key = ROQEET_SCENE_ATTRS[rawKey] ?? rawKey;
    scene[key] = value;
  }
  return Object.keys(scene).length ? scene : undefined;
}

function parseRoqeetRoll(rest: string): DiceRequest | undefined {
  const attrs = parseDirectiveAttrs(rest);
  const bodyNotation = rest
    .replace(/([\p{L}\w.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"']+))/gu, " ")
    .trim()
    .split(/\s+/)[0];
  const notation = attrs["dice"] ?? attrs["d"] ?? attrs["notation"] ?? stripQuotes(bodyNotation ?? rest);
  if (!notation.trim()) return undefined;
  const dc = attrs["dc"] !== undefined && Number.isFinite(Number(attrs["dc"])) ? Number(attrs["dc"]) : undefined;
  const label = attrs["label"]?.trim();
  return {
    notation: notation.trim(),
    ...(dc !== undefined ? { dc } : {}),
    ...(label ? { label } : {}),
  };
}

function rejectAction(
  result: ParsedTurn,
  source: string,
  message: string,
  line?: number,
  code: TurnDiagnostic["code"] = "invalid_action",
) {
  result.diagnostics.push({
    severity: "warning",
    code,
    message,
    source,
    ...(line !== undefined ? { line } : {}),
  });
}

function extractRoqeetDirectives(text: string, result: ParsedTurn): string {
  const lines = text.split(/\r?\n/);
  const kept: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const trimmed = line.trim();
    const directive = /^@([^\s{]+)\s*(.*)$/u.exec(trimmed);
    if (!directive) {
      kept.push(line);
      continue;
    }

    const name = directive[1]!;
    const rest = (directive[2] ?? "").trim();

    if (ROQEET_CHOICES.has(name)) {
      const block = readRoqeetBlock(lines, i, rest);
      if (block) {
        for (const c of parseChoices(block.inner)) result.choices.push(c);
        i = block.end;
        continue;
      }
      rejectAction(result, line, "@선택지 block is incomplete.", i + 1);
      kept.push(line);
      continue;
    }

    const op = ROQEET_STATE_OPS[name];
    if (op) {
      const parsed = parseRoqeetStateOp(op, rest);
      if (parsed) {
        result.stateDelta.push(parsed);
        continue;
      }
      rejectAction(result, line, `@${name} action is incomplete or malformed.`, i + 1);
      continue;
    }

    if (ROQEET_SCENE.has(name)) {
      const scene = parseRoqeetScene(rest);
      if (scene) {
        result.scene = { ...(result.scene ?? {}), ...scene };
        continue;
      }
      rejectAction(result, line, `@${name} action has no scene attributes.`, i + 1);
      continue;
    }

    if (ROQEET_ROLL.has(name)) {
      const roll = parseRoqeetRoll(rest);
      if (roll) {
        result.rolls.push(roll);
        continue;
      }
      rejectAction(result, line, `@${name} action has no dice notation.`, i + 1);
      continue;
    }

    rejectAction(result, line, `Unknown RoQeet action @${name}.`, i + 1, "unknown_action");
    kept.push(line);
  }

  return kept.join("\n");
}

function stripRoqeetDirectivesForDisplay(text: string): string {
  const lines = text.split(/\r?\n/);
  const kept: string[] = [];
  let inChoices = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (inChoices) {
      if (trimmed === "}") inChoices = false;
      continue;
    }

    const directive = /^@([^\s{]+)\s*(.*)$/u.exec(trimmed);
    if (!directive) {
      kept.push(line);
      continue;
    }

    const name = directive[1]!;
    const rest = (directive[2] ?? "").trim();
    if (ROQEET_CHOICES.has(name) && rest.startsWith("{")) {
      if (!rest.includes("}")) inChoices = true;
      continue;
    }
    if (ROQEET_STATE_OPS[name] || ROQEET_SCENE.has(name) || ROQEET_ROLL.has(name)) {
      continue;
    }

    kept.push(line);
  }

  return kept.join("\n");
}

/**
 * AI 출력을 파싱한다(결정 #26). 항상 성공한다 — 구조가 깨져도 narration은 채워진다.
 */
export function parseTurn(text: string): ParsedTurn {
  const result: ParsedTurn = {
    narration: "",
    choices: [],
    stateDelta: [],
    rolls: [],
    diagnostics: [],
    raw: text,
  };
  if (!text) return result;

  let working = text;

  // 1) <choices>...</choices> 블록 추출 + 제거.
  working = working.replace(/<choices\b[^>]*>([\s\S]*?)<\/choices>/gi, (_full, inner: string) => {
    for (const c of parseChoices(inner)) result.choices.push(c);
    return "";
  });

  // 2) <roll>...</roll> 블록(표기가 내부 텍스트) 추출 + 제거.
  working = working.replace(/<roll\b[^>]*>([\s\S]*?)<\/roll>/gi, (_full, inner: string) => {
    const notation = inner.trim();
    if (notation) result.rolls.push({ notation });
    return "";
  });

  // 3) 자기 종결 태그(set/inc/dec/push/unset/scene/roll) 추출 + 제거.
  working = working.replace(/<([\w-]+)\b([^>]*?)\/?>/g, (full, tag: string, attrText: string) => {
    const name = tag.toLowerCase();
    if (STATE_OPS.has(name)) {
      const attrs = parseAttrs(attrText);
      const key = attrs["key"] ?? attrs["name"] ?? "";
      if (key) {
        const op = name as StateOp["op"];
        const value = attrs["value"] ?? attrs["val"];
        const entity = attrs["entity"] ?? attrs["npc"];
        const opObj: StateOp = value !== undefined ? { op, key, value } : { op, key };
        if (entity) opObj.entity = entity;
        result.stateDelta.push(opObj);
      } else {
        rejectAction(result, full, `<${name}> action has no key.`);
      }
      return "";
    }
    if (name === "scene") {
      const attrs = parseAttrs(attrText);
      result.scene = { ...(result.scene ?? {}), ...attrs };
      return "";
    }
    if (name === "roll") {
      const attrs = parseAttrs(attrText);
      const notation = attrs["dice"] ?? attrs["d"] ?? attrs["notation"] ?? "";
      if (notation) {
        const dc = attrs["dc"] !== undefined && Number.isFinite(Number(attrs["dc"])) ? Number(attrs["dc"]) : undefined;
        const label = attrs["label"]?.trim();
        result.rolls.push({
          notation,
          ...(dc !== undefined ? { dc } : {}),
          ...(label ? { label } : {}),
        });
      }
      else rejectAction(result, full, "<roll> action has no dice notation.");
      return "";
    }
    // 알 수 없는 태그: 그대로 둔다(narration 일부로 살림, 결정 #26 관대함).
    return full;
  });

  // 4) <narration>...</narration>는 언랩(내용 보존), 닫히지 않은 잔여 태그도 제거.
  working = working.replace(/<\/?narration\b[^>]*>/gi, "");

  // 4.5) RoQeet 줄 명령 추출 + 제거. 기존 태그와 병행 지원한다.
  working = extractRoqeetDirectives(working, result);

  // 5) 남은 텍스트 = narration. 빈 줄 정리 + 트림.
  result.narration = working.replace(/\n{3,}/g, "\n\n").trim();
  return result;
}

/**
 * 스트리밍 중 표시용으로 구조 태그를 숨긴다(결정 #26/#14). 완료 전에는 parseTurn을
 * 돌리지 않고(아직 태그가 미완성일 수 있음) 이 함수로 본문만 점진 표시한다.
 *
 * 닫힌 블록 태그(choices/roll)는 통째로 제거하고, 자기 종결 구조 태그(set/scene 등)도
 * 제거한다. 스트리밍 도중 "아직 닫히지 않은" 마지막 태그 조각(`<cho` 또는 `<choices>`
 * 뒤 미완성 내용)은 잘라내, 깨진 태그가 화면에 깜빡이지 않게 한다(결정 #26 관대함).
 */
export function stripTurnTagsForDisplay(text: string): string {
  if (!text) return "";
  let s = text;
  // 완결된 블록 태그 제거.
  s = s.replace(/<choices\b[^>]*>[\s\S]*?<\/choices>/gi, "");
  s = s.replace(/<roll\b[^>]*>[\s\S]*?<\/roll>/gi, "");
  // 완결된 자기 종결/구조 태그 제거(set/inc/dec/push/unset/scene/roll).
  s = s.replace(/<(?:set|inc|dec|push|unset|scene|roll)\b[^>]*?\/?>/gi, "");
  // narration 래퍼는 언랩.
  s = s.replace(/<\/?narration\b[^>]*>/gi, "");
  // RoQeet 턴 명령도 화면 본문에서는 숨긴다.
  s = stripRoqeetDirectivesForDisplay(s);
  // 스트리밍 꼬리: 아직 닫히지 않은 마지막 '<...' 조각을 잘라 깨진 태그 깜빡임 방지.
  const lastOpen = s.lastIndexOf("<");
  if (lastOpen !== -1 && s.indexOf(">", lastOpen) === -1) {
    s = s.slice(0, lastOpen);
  }
  return s.replace(/\n{3,}/g, "\n\n");
}

/**
 * 주사위 표기("NdM+K")를 시드 RNG로 굴린다(결정 #4 재현성). 모듈이 applyTurn에서
 * rolls를 실제 값으로 변환할 때 쓴다. 잘못된 표기는 0 반환.
 */
export function rollDice(notation: string, random: () => number = Math.random): number {
  const m = /^(\d*)d(\d+)([+-]\d+)?$/i.exec(notation.trim());
  if (!m) return 0;
  const count = m[1] ? parseInt(m[1], 10) : 1;
  const sides = parseInt(m[2]!, 10);
  const modifier = m[3] ? parseInt(m[3], 10) : 0;
  if (count < 1 || count > 100 || sides < 1) return 0; // DoS 방어 상한
  let total = modifier;
  for (let i = 0; i < count; i++) total += 1 + Math.floor(random() * sides);
  return total;
}

/**
 * 상태 연산 목록을 상태 객체에 적용한다(결정 #26/#28). 순수 함수 — 새 객체를
 * 반환하지 않고 얕은 복사본을 만들어 변경한다(모듈이 결과를 저장).
 */
export function applyStateDelta(
  state: Record<string, unknown>,
  ops: StateOp[],
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...state };
  for (const { op, key, value } of ops) {
    switch (op) {
      case "set":
        next[key] = coerce(value);
        break;
      case "inc":
        next[key] = toNum(next[key]) + toNum(value);
        break;
      case "dec":
        next[key] = toNum(next[key]) - toNum(value);
        break;
      case "push": {
        const arr = Array.isArray(next[key]) ? [...(next[key] as unknown[])] : [];
        arr.push(coerce(value));
        next[key] = arr;
        break;
      }
      case "unset":
        delete next[key];
        break;
    }
  }
  return next;
}

/** 문자열 값을 number/boolean으로 자연 변환(가능할 때). */
function coerce(value: string | undefined): unknown {
  if (value === undefined) return true; // 플래그
  if (value === "true") return true;
  if (value === "false") return false;
  if (value !== "" && !Number.isNaN(Number(value))) return Number(value);
  return value;
}

function toNum(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}
